import React from 'react';
import { fmtInTz } from '../coachingTime.js';
import { COURSE_LEVELS, journeyLabel } from '../journeyConfig.js';
import { durationLabel, metric as metricOf, ROUTE_STATES } from '../journey.js';

// Mi recorrido: the person's journey in their space (OIKE_JOURNEY=on). The same
// components render /espacio and the coach's preview. They only show what the
// server sent (shared/journey.ts, shared/personSpace.ts): they compute no
// permission and invent no number. Every indicator carries its words ("3 de 8
// sesiones realizadas"); color is never the only signal (icons and text too);
// movement respects prefers-reduced-motion (portal.css).

const tzOf = () => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; } };
const fmtDay = (iso) => (iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00Z`).toLocaleDateString('es', { day: 'numeric', month: 'short' }) : '');

/** A ring that shows done of total; the label under it says what it counts. Nothing without a defined total. */
export function ProgressRing({ metric, size = 64, label = true }) {
  if (!metric) return null;
  const stroke = 7;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const ratio = metric.total ? metric.done / metric.total : 0;
  return (
    <figure className="jr-ring" style={{ margin: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={metric.label}>
        <circle cx={size / 2} cy={size / 2} r={r} className="jr-ring-track" strokeWidth={stroke} fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} className="jr-ring-fill" strokeWidth={stroke} fill="none" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - ratio)} transform={`rotate(-90 ${size / 2} ${size / 2})`} />
        <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" className="jr-ring-text" aria-hidden="true">{metric.finished ? '✓' : `${metric.done}/${metric.total}`}</text>
      </svg>
      {label && <figcaption className="jr-ring-label">{metric.label}</figcaption>}
    </figure>
  );
}

/** A bar inside a journey: always with its words. */
export function MetricBar({ metric, title }) {
  if (!metric) return null;
  const pct = Math.round((metric.done / metric.total) * 100);
  return (
    <div className="jr-bar">
      {title && <span className="jr-bar-title">{title}</span>}
      <div className="track" role="progressbar" aria-label={title || metric.label} aria-valuemin={0} aria-valuemax={metric.total} aria-valuenow={metric.done} aria-valuetext={metric.label}>
        <div className="fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="jr-bar-label">{metric.label}</span>
    </div>
  );
}

const STEP_ICON = { done: '✓', current: '●', ahead: '' };
const STEP_WORD = { done: 'Hecho', current: 'Ahora', ahead: 'Más adelante' };

/** The milestones of a process, in order: done (✓), now (●) and ahead (empty), each also in words. */
export function MilestonePath({ steps = [], tz = tzOf(), title = 'Hitos' }) {
  if (!steps.length) return null;
  return (
    <ol className="jr-path" aria-label={title}>
      {steps.map(s => (
        <li key={s.key} className={`jr-step ${s.state}`} aria-current={s.state === 'current' ? 'step' : undefined}>
          <span className="jr-dot" aria-hidden="true">{STEP_ICON[s.state]}</span>
          <span className="jr-step-text">
            <b>{s.title}</b>
            <span className="muted"><span className="sr-only">{STEP_WORD[s.state]}: </span>{s.detail}{s.at && s.state !== 'ahead' ? ` · ${s.state === 'done' ? fmtDay(s.at) : fmtInTz(s.at, tz)}` : ''}</span>
          </span>
        </li>
      ))}
    </ol>
  );
}

/** Approved text, with paragraphs, lists and bold only (never HTML from the record). */
export function ApprovedText({ text }) {
  const blocks = String(text || '').split(/\n{2,}/).map(b => b.trim()).filter(Boolean);
  const inline = (line, k) => line.split(/(\*\*[^*]+\*\*)/g).map((part, i) => (part.startsWith('**') && part.endsWith('**') ? <b key={`${k}-${i}`}>{part.slice(2, -2)}</b> : <React.Fragment key={`${k}-${i}`}>{part}</React.Fragment>));
  return <>{blocks.map((b, i) => {
    const lines = b.split('\n');
    if (lines.every(l => /^\s*[-*]\s+/.test(l))) return <ul key={i} className="plain">{lines.map((l, j) => <li key={j}>{inline(l.replace(/^\s*[-*]\s+/, ''), `${i}-${j}`)}</li>)}</ul>;
    return <p key={i} style={{ margin: '0 0 10px' }}>{lines.map((l, j) => <React.Fragment key={j}>{j > 0 && <br />}{inline(l, `${i}-${j}`)}</React.Fragment>)}</p>;
  })}</>;
}

// ── Cards of each process (one service each: never mixed) ──

function primaryOf(card) {
  const p = card.progress;
  if (!p) return null;
  return card.kind === 'diagnostic' ? p.metric : card.kind === 'coaching' ? p.sessions : null;
}

function JourneyCard({ card, onOpen, tz }) {
  const p = card.progress || {};
  const primary = primaryOf(card);
  const steps = card.kind === 'diagnostic' ? p.stages : p.milestones;
  return (
    <article className={`card jr-card kind-${card.kind}`} aria-label={card.name}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <span className={`badge kind-${card.kind}`}>{card.type}</span>
        <span className={`badge ${card.finished ? '' : 'on'}`}>{card.statusLabel}</span>
      </div>
      <div className="jr-card-head">
        {primary ? <ProgressRing metric={primary} label={false} /> : <span className="jr-ring-empty" aria-hidden="true">·</span>}
        <div style={{ minWidth: 0 }}>
          <h3>{card.name}</h3>
          <p className="jr-primary">{primary ? primary.label : card.kind === 'coaching' ? 'Las sesiones se definen al empezar' : card.kind === 'sponsor' ? 'Vista grupal del equipo' : ''}</p>
        </div>
      </div>
      {card.kind === 'coaching' && <>
        <MetricBar metric={p.individual} title="Tus sesiones individuales" />
        <MetricBar metric={p.activities} title="Actividades" />
        <MetricBar metric={p.goals} title="Objetivos compartidos" />
      </>}
      {steps?.length > 0 && (
        <details className="jr-details">
          <summary>Ver el camino ({steps.filter(s => s.state === 'done').length} de {steps.length} {card.kind === 'diagnostic' ? 'etapas' : 'hitos'})</summary>
          <MilestonePath steps={steps} tz={tz} title={`Hitos de ${card.name}`} />
        </details>
      )}
      {card.next && <p className="jr-next-line"><span className="muted">{card.finished ? 'Disponible: ' : 'Siguiente: '}</span><b>{card.next.title}</b>{card.next.when ? <span className="muted"> · {card.next.whenKind === 'deadline' ? `hasta el ${fmtDay(card.next.when)}` : fmtInTz(card.next.when, tz)}</span> : null}</p>}
      <button className={`btn ${card.finished ? '' : 'primary'}`} onClick={() => onOpen(card)}>Abrir</button>
    </article>
  );
}

// ── Tu ruta recomendada ──

const STATE_ICON = { recommended: '☆', assigned: '◆', in_progress: '▸', completed: '✓' };

function RouteItem({ item, onStart, onOpenCourse, courses, preview }) {
  const course = courses.find(c => c.courseId === item.courseId || c.id === item.enrollmentId);
  const m = course ? metricOf(course.done, course.total, 'lessons') : null;
  const assignedLabel = item.state === 'recommended' ? 'Sugerencia opcional' : course?.startedBy === 'self' ? 'Lo empezaste tú' : 'Asignado por tu acompañante';
  return (
    <li className={`jr-route-item ${item.state}`}>
      <span className="jr-route-icon" aria-hidden="true">{STATE_ICON[item.state]}</span>
      <div className="jr-route-body">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <b className="jr-route-name">{item.name}</b>
          <span className={`badge ${item.state === 'recommended' ? 'kind-proposal' : item.state === 'completed' ? '' : 'on'}`}>{item.state === 'recommended' && item.kind === 'basic' ? 'Ruta básica' : ROUTE_STATES[item.state]}</span>
        </div>
        <div className="muted">{[item.level && journeyLabel(COURSE_LEVELS, item.level), item.minutes && `unos ${durationLabel(item.minutes)}`, assignedLabel].filter(Boolean).join(' · ')}</div>
        {item.reason && <p className="jr-reason">{item.reason}</p>}
        {m && item.state !== 'recommended' && <MetricBar metric={m} />}
        <div className="row" style={{ marginTop: 6 }}>
          {item.state === 'recommended' && item.offer && (preview ? <span className="muted">Puede empezarlo desde su espacio</span> : <button className="btn small primary" onClick={() => onStart(item)}>Empezar</button>)}
          {item.state !== 'recommended' && course && onOpenCourse && <button className="btn small" onClick={() => onOpenCourse(course, item.state === 'completed' ? null : course.next?.id || null)}>{item.state === 'completed' ? 'Ver curso' : item.state === 'assigned' ? 'Empezar' : 'Continuar'}</button>}
        </div>
      </div>
    </li>
  );
}

/** The section title of the route: «Ruta básica» when it is the common one (the same for everybody), never "for you". */
export const routeTitle = (route) => (route?.mode === 'basic' ? 'Ruta básica' : 'Tu ruta recomendada');

export function RouteList({ journey, courses = [], onStart, onOpenCourse, preview = false }) {
  // Recommendations off (OIKE_JOURNEY_RECOMMENDATIONS): no route at all, not even an empty one
  if (journey.recommendations === false) return null;
  const route = journey.route || { mode: 'empty', items: [] };
  const intro = route.mode === 'personalized' ? 'Cursos elegidos a partir de los resultados que se compartieron contigo. Son sugerencias: tú decides si empezar.'
    : route.mode === 'basic' ? (journey.personalizedState === 'waiting' || journey.personalizedState === 'preparing' ? 'Mientras se preparan tus recomendaciones personalizadas, puedes empezar cursos de la ruta básica de Oike, la misma para todas las personas.' : 'Cursos para empezar cuando quieras. Es la misma ruta para todas las personas.')
    : '';
  return (
    <section className="card" aria-labelledby="jr-route-title">
      <h2 id="jr-route-title">{routeTitle(route)}</h2>
      {intro && <p className="muted" style={{ marginTop: -4 }}>{intro}</p>}
      {route.mode === 'empty'
        ? <p className="muted">Todavía no hay cursos para tu ruta. Cuando tu coach los prepare, aparecen acá. Si te interesa un tema, escríbele.</p>
        : <ol className="jr-route">{route.items.map(i => <RouteItem key={i.courseId} item={i} onStart={onStart} onOpenCourse={onOpenCourse} courses={courses} preview={preview} />)}</ol>}
      {journey.personalizedState === 'preparing' && <p className="muted">Tus recomendaciones personalizadas están en preparación.</p>}
    </section>
  );
}

// ── Results of the Diagnostic (inside its detail) ──

export function ResultsPanel({ result, journey, courses = [], onOpenCourse, onStart = null }) {
  if (!result) return null;
  if (result.status !== 'shared') {
    return (
      <div className="card">
        <h2>Tus resultados</h2>
        <p className="muted" style={{ margin: 0 }}>{result.status === 'report'
          ? 'Ya puedes ver el informe compartido más abajo. Tu ficha personal todavía no se compartió contigo: cuando esté aprobada y lista, aparece acá con tus fortalezas, tus áreas para trabajar y tus próximos pasos.'
          : 'Tus resultados todavía no se compartieron. Cuando estén aprobados y listos, aparecen acá.'}</p>
      </div>
    );
  }
  const related = (journey?.route?.items || []).filter(i => (result.related || []).includes(i.courseId));
  return (
    <div className="card jr-results">
      <h2>Tus resultados</h2>
      <p className="muted" style={{ marginTop: -4 }}>De tu ficha aprobada y compartida contigo. Describen lo observado en el diagnóstico, no una calificación tuya.</p>
      {result.strengths && <section className="jr-result-block strengths"><h3><span aria-hidden="true">✦ </span>Fortalezas</h3><ApprovedText text={result.strengths} /></section>}
      {result.focus && <section className="jr-result-block focus"><h3><span aria-hidden="true">◎ </span>Áreas para trabajar</h3><ApprovedText text={result.focus} /></section>}
      {result.nextSteps?.length > 0 && <section className="jr-result-block steps"><h3><span aria-hidden="true">→ </span>Próximos pasos</h3><ul className="plain">{result.nextSteps.map((s, i) => <li key={i}>{s.charAt(0).toUpperCase() + s.slice(1)}</li>)}</ul></section>}
      {!result.strengths && !result.focus && <p className="muted">Abre tu ficha completa más abajo.</p>}
      {related.length > 0 && (
        <section className="jr-result-block">
          <h3>Cursos relacionados</h3>
          <ol className="jr-route">{related.map(i => <RouteItem key={i.courseId} item={i} onStart={onStart || (() => {})} onOpenCourse={onOpenCourse} courses={courses} preview={!onStart} />)}</ol>
        </section>
      )}
    </div>
  );
}

// ── Inicio: Mi recorrido ──

/**
 * What a person needs on entering: the one next step, their processes with what
 * each one counts, their courses, their route and small recognitions.
 */
export function JourneyHome({ view, courses = [], onOpen, onOpenCourse, onStart, preview = false, updates = null }) {
  const j = view.journey;
  const tz = view.person?.timeZone || tzOf();
  // Her own processes; the teams she leads go apart (another role, never mixed with her own progress)
  const active = (view.active || []).filter(c => c.kind !== 'sponsor');
  const led = (view.active || []).filter(c => c.kind === 'sponsor');
  const next = j.next;
  const openNext = () => {
    if (!next) return;
    if (next.routeCourseId) { const item = (j.route?.items || []).find(i => i.courseId === next.routeCourseId); if (item && onStart) onStart(item); return; }
    if (next.courseId) { const c = courses.find(x => x.id === next.courseId); if (c && onOpenCourse) onOpenCourse(c, next.lessonId || null); return; }
    const card = active.find(c => c.key === next.cardKey);
    if (card) onOpen(card);
  };
  const assigned = courses.filter(c => c.status !== 'completed' && !(j.route?.items || []).some(i => i.enrollmentId === c.id || i.courseId === c.courseId));
  const routeItems = j.recommendations === false ? [] : (j.route?.items || []);
  const inCourses = courses.some(c => c.status !== 'completed');
  // Nothing running and nothing to suggest: one calm message instead of empty blocks
  const calm = !active.length && !inCourses && !routeItems.length && !next && !led.length;
  return (
    <>
      {calm ? (
        <section className="card jr-calm" aria-labelledby="jr-calm-title">
          <h2 id="jr-calm-title">Por ahora no tienes nada en curso</h2>
          <p className="muted" style={{ margin: 0 }}>Cuando empieces un acompañamiento, un diagnóstico o un curso, acá vas a ver tu avance y tu próximo paso. No tienes que hacer nada ahora.</p>
        </section>
      ) : <>
      {/* Someone who only leads a team has no personal next step: the team's goes in its own view */}
      {(active.length > 0 || inCourses || next || !led.length) && <section className="jr-hero" aria-labelledby="jr-next-title">
        <div className="lrn-eyebrow" id="jr-next-title">Tu próximo paso</div>
        {next ? <>
          <h2 className="jr-hero-title">{next.title}</h2>
          <p className="jr-hero-sub">{next.where}{next.when ? ` · ${next.whenKind === 'deadline' ? `hasta el ${fmtDay(next.when)}` : fmtInTz(next.when, tz)}` : ''}</p>
          {!preview && <button className="btn primary" onClick={openNext}>{next.routeCourseId ? 'Empezar' : 'Ir a este paso'}</button>}
          <p className="muted" style={{ margin: '10px 0 0' }}>Un paso a la vez: lo demás puede esperar.</p>
        </> : <p style={{ margin: '6px 0 0' }}>No tienes pasos pendientes ahora. Cuando haya algo nuevo, aparece acá.</p>}
      </section>}

      {/* With courses but no process, the courses are what is running: no "nothing in progress" line */}
      {(active.length > 0 || (!inCourses && !led.length)) && (
        <section aria-labelledby="jr-processes" className="jr-section">
          <h2 id="jr-processes">Tus recorridos</h2>
          {!active.length ? <div className="card muted">No tienes acompañamientos ni diagnósticos en curso.</div>
            : <div className="space-grid">{active.map(c => <JourneyCard key={c.key} card={c} onOpen={onOpen} tz={tz} />)}</div>}
        </section>
      )}
      </>}

      {led.length > 0 && (
        <section aria-labelledby="jr-led" className="jr-section">
          <h2 id="jr-led">Equipos que lideras</h2>
          <p className="muted" style={{ marginTop: -4 }}>La vista grupal de cada equipo: nunca lo individual de cada persona.</p>
          <div className="space-grid">{led.map(c => (
            <article key={c.key} className="card jr-card kind-sponsor" aria-label={c.name}>
              <div className="row" style={{ justifyContent: 'space-between' }}><span className="badge kind-sponsor">{c.type}</span><span className={`badge ${c.finished ? '' : 'on'}`}>{c.statusLabel}</span></div>
              <h3 style={{ margin: '8px 0' }}>{c.name}</h3>
              <button className="btn primary small" onClick={() => onOpen(c)}>Ver el equipo</button>
            </article>
          ))}</div>
        </section>
      )}

      {assigned.length > 0 && (
        <section className="card" aria-labelledby="jr-courses">
          <h2 id="jr-courses">Tus cursos asignados</h2>
          <ul className="jr-course-list">
            {assigned.map(c => (
              <li key={c.id}>
                <ProgressRing metric={metricOf(c.done, c.total, 'lessons')} size={52} label={false} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <b>{c.courseName}</b>
                  <div className="muted">{c.startedBy === 'self' ? 'Lo empezaste tú' : 'Asignado por tu acompañante'}{c.processName ? ` · de «${c.processName}»` : ''}</div>
                  <div className="muted">{metricOf(c.done, c.total, 'lessons')?.label || 'Sin lecciones todavía'}{c.waiting ? ` · ${c.waiting === 1 ? '1 entrega en revisión' : `${c.waiting} entregas en revisión`}` : ''}</div>
                </div>
                {onOpenCourse && <button className="btn small" onClick={() => onOpenCourse(c, c.next?.id || null)}>{c.lastActivityAt ? 'Continuar' : 'Empezar'}</button>}
              </li>
            ))}
          </ul>
        </section>
      )}

      <RouteList journey={j} courses={courses} onStart={onStart} onOpenCourse={onOpenCourse} preview={preview} />

      {j.recognitions?.length > 0 && (
        <section className="card" aria-labelledby="jr-recog">
          <h2 id="jr-recog">Lo que ya hiciste</h2>
          <ul className="jr-recognitions">{j.recognitions.slice(0, 6).map(r => <li key={r.key}><span aria-hidden="true">✓</span> {r.text}<span className="muted"> · {r.where}</span></li>)}</ul>
        </section>
      )}
      {updates}
    </>
  );
}

/** Historial: finished processes with their milestones, and finished courses. */
export function JourneyHistory({ view, courses = [], onOpen, onOpenCourse }) {
  const tz = view.person?.timeZone || tzOf();
  const done = courses.filter(c => c.status === 'completed');
  if (!view.history?.length && !done.length) return <div className="card muted">Todavía no hay procesos ni cursos terminados.</div>;
  return (
    <>
      {view.history?.length > 0 && <div className="space-grid">{view.history.map(c => <JourneyCard key={c.key} card={c} onOpen={onOpen} tz={tz} />)}</div>}
      {done.length > 0 && (
        <section className="card" style={{ marginTop: 14 }} aria-labelledby="jr-done-courses">
          <h2 id="jr-done-courses">Cursos terminados</h2>
          <ul className="jr-course-list">{done.map(c => (
            <li key={c.id}>
              <ProgressRing metric={metricOf(c.done, c.total, 'lessons')} size={52} label={false} />
              <div style={{ flex: 1 }}><b>{c.courseName}</b><div className="muted">{metricOf(c.done, c.total, 'lessons')?.label}{c.completedAt ? ` · terminado el ${fmtDay(c.completedAt)}` : ''}</div></div>
              {onOpenCourse && <button className="btn small" onClick={() => onOpenCourse(c, null)}>Ver curso</button>}
            </li>
          ))}</ul>
        </section>
      )}
    </>
  );
}
